import React, { createContext, useContext, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';

const ApplicationStateContext = createContext(null);

export const ApplicationStateProvider = ({ children }) => {
  const queryClient = useQueryClient();
  
  // Centralized query refresh with rate limiting
  const refreshApplicationData = useCallback((options = {}) => {
    const {
      userId = null,
      force = false,
      includeEvents = true,
      includeVideos = true,
      includePulse = true,
      includePersonalizedFeed = true,
      includeUserProfile = true,
      includeArticles = true
    } = options;
    
    // Rate limiting
    const now = Date.now();
    const lastRefresh = parseInt(sessionStorage.getItem('lastGlobalRefresh') || '0', 10);
    
    if (!force && now - lastRefresh < 2000) {
      console.log('ApplicationState: Skipping refresh due to rate limiting');
      return;
    }
    
    console.log('ApplicationState: Performing global refresh');
    sessionStorage.setItem('lastGlobalRefresh', now.toString());
    
    // Common queries
    if (includeEvents) queryClient.invalidateQueries(['events']);
    if (includeVideos) queryClient.invalidateQueries(['videos']);
    if (includePulse) queryClient.invalidateQueries(['pulse']);
    if (includeArticles) queryClient.invalidateQueries(['articles']);
    
    // User-specific queries
    if (userId) {
      if (includePersonalizedFeed) queryClient.invalidateQueries(['personalizedFeed']);
      if (includeUserProfile) queryClient.invalidateQueries(['userProfile']);
    }
    
    // Also refresh the auth session
    supabase.auth.refreshSession();
  }, [queryClient]);
  
  // Reset stuck UI states
  const resetUIStates = useCallback(() => {
    console.log('ApplicationState: Resetting stuck UI states');
    
    // Reset filter states
    queryClient.setQueryData(['loadingStates'], {
      isFilterChanging: false,
      isSaving: false
    });
    
    // Clear any stale flags
    if (Date.now() - parseInt(localStorage.getItem('preferencesUpdatedAt') || '0', 10) > 300000) {
      localStorage.removeItem('preferencesJustUpdated');
      localStorage.removeItem('preferencesUpdatedAt');
    }
  }, [queryClient]);
  
  // Force a hard reload if necessary
  const forceReload = useCallback(() => {
    console.log('ApplicationState: Forcing page reload');
    window.location.reload();
  }, []);
  
  // Create the context value
  const value = {
    refreshApplicationData,
    resetUIStates,
    forceReload
  };
  
  return (
    <ApplicationStateContext.Provider value={value}>
      {children}
    </ApplicationStateContext.Provider>
  );
};

export const useApplicationState = () => {
  const context = useContext(ApplicationStateContext);
  if (context === undefined) {
    throw new Error('useApplicationState must be used within ApplicationStateProvider');
  }
  return context;
}; 